import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { useAuth } from "@/contexts/AuthContext";
import { normalizePhoneE164 } from "@/lib/phone-format";
import type { DBFreelancerContact, FreelancerContactRole } from "@/lib/db-types";

/**
 * VIB-2934: kontaktpersoner for en frilanser eller et firma. Audit skrives av
 * DB-triggeren (aktør = innlogget bruker); updated_by settes fra useAuth.
 */

export function useFreelancerContacts(freelancerId: string | undefined) {
  return useQuery({
    queryKey: ["freelancer-contacts", freelancerId],
    enabled: !!freelancerId,
    queryFn: async (): Promise<DBFreelancerContact[]> => {
      const { data, error } = await supabase
        .from("freelancer_contacts")
        .select("*")
        .eq("freelancer_id", freelancerId!)
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as DBFreelancerContact[];
    },
  });
}

export interface FreelancerContactInput {
  name: string;
  role: FreelancerContactRole;
  role_note: string | null;
  email: string | null;
  phone: string | null;
}

const clean = (v: string | null) => (v?.trim() ? v.trim() : null);

export function useSaveFreelancerContact() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({ id, freelancerId, input, sortOrder }: { id?: string; freelancerId: string; input: FreelancerContactInput; sortOrder?: number }) => {
      if (!input.name.trim()) throw new Error("Navn mangler");
      const phone = clean(input.phone);
      const e164 = phone ? normalizePhoneE164(phone) : null;
      const row = {
        name: input.name.trim(),
        role: input.role,
        role_note: input.role === "other" ? clean(input.role_note) : null,
        email: clean(input.email)?.toLowerCase() ?? null,
        // Norske nummer lagres som +47XXXXXXXX og grupperes ved visning; utenlandske lagres som skrevet.
        phone: e164?.startsWith("+47") ? e164 : phone,
        updated_by: user?.id ?? null,
      };
      const { error } = id
        ? await supabase.from("freelancer_contacts").update(row).eq("id", id)
        : await supabase.from("freelancer_contacts").insert({ ...row, freelancer_id: freelancerId, sort_order: sortOrder ?? 0 });
      if (error) throw error;
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "freelancer_contacts", { freelancerId: vars.freelancerId });
      toast.success(vars.id ? "Kontaktpersonen er oppdatert" : "Kontaktpersonen er lagt til");
    },
    onError: (err: Error) => toast.error(`Kunne ikke lagre kontaktpersonen: ${err.message}`),
  });
}

export function useDeleteFreelancerContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }: { id: string; freelancerId: string }) => {
      const { error } = await supabase.from("freelancer_contacts").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "freelancer_contacts", { freelancerId: vars.freelancerId });
      toast.success("Kontaktpersonen er fjernet");
    },
    onError: (err: Error) => toast.error(`Kunne ikke fjerne kontaktpersonen: ${err.message}`),
  });
}
